import config from "./config";

type BundleRegion = "nz" | "au" | "uk" | "us";

type BundlePrice = {
  amount?: string | number | null;
} | null | undefined;

type BundleLike = object;

export type BundleMetaobjectField = {
  key: string;
  value?: string | null;
  jsonValue?: unknown;
};

export function getBundleRegion(countryCode = config.countryCode): BundleRegion {
  if (countryCode === "AU") {
    return "au";
  }
  if (countryCode === "GB" || countryCode === "UK") {
    return "uk";
  }
  if (countryCode === "US") {
    return "us";
  }
  return "nz";
}

export function getBundlePriceFieldKey(countryCode = config.countryCode) {
  return `bundle_price_${getBundleRegion(countryCode)}`;
}

export function getBundleDisplayFieldKey(countryCode = config.countryCode) {
  return `display_bundle_in_${getBundleRegion(countryCode)}`;
}

export function getRegionalBundlePrice(bundle: BundleLike, countryCode = config.countryCode) {
  const price = (bundle as Record<string, unknown>)[getBundlePriceFieldKey(countryCode)] as BundlePrice;
  return parsePositiveAmount(price?.amount);
}

export function isRegionalBundleEnabled(bundle: BundleLike, countryCode = config.countryCode) {
  return parseBoolean((bundle as Record<string, unknown>)[getBundleDisplayFieldKey(countryCode)]);
}

export function isBundleAvailableForRegion(bundle: BundleLike, countryCode = config.countryCode) {
  return isActiveBundle(bundle) &&
    isRegionalBundleEnabled(bundle, countryCode) &&
    getRegionalBundlePrice(bundle, countryCode) !== null;
}

export function getRegionalBundleFieldPrice(
  fields: BundleMetaobjectField[],
  countryCode = config.countryCode,
) {
  const field = getField(fields, getBundlePriceFieldKey(countryCode));
  if (!field) {
    return null;
  }

  if (typeof field.jsonValue === "object" && field.jsonValue) {
    const money = field.jsonValue as { amount?: string | number | null };
    return parsePositiveAmount(money.amount);
  }

  return parsePositiveAmount(field.value);
}

export function isRegionalBundleFieldEnabled(
  fields: BundleMetaobjectField[],
  countryCode = config.countryCode,
) {
  const field = getField(fields, getBundleDisplayFieldKey(countryCode));
  return parseBoolean(field?.jsonValue ?? field?.value);
}

export function isBundleFieldsAvailableForRegion(
  fields: BundleMetaobjectField[],
  countryCode = config.countryCode,
) {
  return isRegionalBundleFieldEnabled(fields, countryCode) &&
    getRegionalBundleFieldPrice(fields, countryCode) !== null;
}

function getField(fields: BundleMetaobjectField[], key: string) {
  return fields.find((field) => field.key === key) ?? null;
}

function isActiveBundle(bundle: BundleLike) {
  const data = bundle as {
    status?: string | null;
    capabilities?: {
      publishable?: {
        status?: string | null;
      } | null;
    } | null;
  };
  const status = data.status ?? data.capabilities?.publishable?.status;
  return !status || status === "ACTIVE";
}

function parsePositiveAmount(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  return amount;
}

function parseBoolean(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return false;
}
